import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Grid, Button, Skeleton, Paper, Box, Typography, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import CustomDateFilterDialog from 'components/popups/CustomDateFilterDialog';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import styles from './MultiShopDashboard.module.css';
import SalesReportsService from 'services/SalesReportsService';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { useTranslation } from 'react-i18next';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'const';
import dasboard_purchase_icon from '../../../images/dashboard_purchase.svg';
import dasboard_expense_icon from '../../../images/dashboard_add_expense.svg';
import dasboard_item_icon from '../../../images/dashboard_add_product.svg';

const TIME_RANGE = {
  TODAY: 'today',
  WEEK: 'week',
  MONTH: 'month',
  SIX_MONTHS: 'six-months',
  CUSTOM: 'custom',
};

const QuickActionCard = ({ icon: Icon, title, description, buttonText, onClick }) => (
  <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
    <Paper
      sx={{
        p: 4,
        textAlign: 'center',
        borderRadius: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        {typeof Icon === 'string' ? (
          <img src={Icon} alt={title} style={{ height: 120, objectFit: 'contain' }} />
        ) : (
          <Box sx={{ bgcolor: '#e8eaf6', p: 3, borderRadius: '50%' }}>
            <Icon sx={{ fontSize: 48, color: '#1975d1' }} />
          </Box>
        )}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
        {description}
      </Typography>
      <Box sx={{ mt: 'auto' }}>
        <Button
          variant="outlined"
          sx={{ color: '#1975d1', borderColor: '#1975d1', borderRadius: '20px', textTransform: 'none', px: 4 }}
          onClick={onClick}
        >
          {buttonText}
        </Button>
      </Box>
    </Paper>
  </Grid>
);

function MultiShopDashboard() {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const handleFilterSelect = (range) => {
    if (range === TIME_RANGE.CUSTOM) {
      setCustomDialogOpen(true);
    } else {
      setFilterDateRange(range);
    }
    handleFilterClose();
  };

  const [filterDateRange, setFilterDateRange] = useState(TIME_RANGE.TODAY);
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);

  const dateRangeMap = useMemo(
    () => ({
      [TIME_RANGE.TODAY]: DateTimeHelpers.getTodayStrInYMD(),
      [TIME_RANGE.WEEK]: DateTimeHelpers.getWeekStr(),
      [TIME_RANGE.MONTH]: DateTimeHelpers.getMonthStr(),
      [TIME_RANGE.SIX_MONTHS]: DateTimeHelpers.getSixMonthStr(),
    }),
    []
  );

  const loadMultiShopSummary = useCallback(
    async (range, customFrom, customTo) => {
      setIsLoading(true);
      try {
        let from_date, to_date;
        if (range === TIME_RANGE.CUSTOM) {
          if (!customFrom || !customTo) return;
          from_date = DateTimeHelpers.getDateYMD(customFrom);
          to_date = DateTimeHelpers.getDateYMD(customTo);
        } else {
          from_date = dateRangeMap[range];
          to_date = DateTimeHelpers.getTodayStrInYMD();
        }

        const params = {
          filter_start_time: DateTimeHelpers.startOfDay(from_date),
          filter_end_time: DateTimeHelpers.endOfDay(to_date),
        };
        const res = await SalesReportsService.getMultiShopSummary(params);
        setSummaryData(res || []);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [dateRangeMap]
  );

  useEffect(() => {
    loadMultiShopSummary(filterDateRange, customFromDate, customToDate);
  }, [filterDateRange, customFromDate, customToDate, loadMultiShopSummary]);



  const totals = useMemo(() => {
    return summaryData.reduce(
      (acc, curr) => {
        acc.total_amount += parseFloat(curr.total_amount || 0);
        acc.no_of_invoices += parseInt(curr.no_of_invoices || 0);
        acc.total_cost += parseFloat(curr.total_cost || 0);
        acc.total_tax += parseFloat(curr.total_tax || 0);
        acc.total_sales_tax += parseFloat(curr.total_sales_tax || 0);
        acc.total_sales_cess += parseFloat(curr.total_sales_cess || 0);
        acc.profit += parseFloat(curr.profit || 0);
        acc.damage_value += parseFloat(curr.damage_value || 0);
        acc.total_discount += parseFloat(curr.total_discount || 0);
        acc.total_expense += parseFloat(curr.total_expense || 0);
        if (!acc.currency) acc.currency = curr.currency;
        return acc;
      },
      {
        total_amount: 0,
        no_of_invoices: 0,
        total_cost: 0,
        total_tax: 0,
        total_sales_tax: 0,
        total_sales_cess: 0,
        profit: 0,
        damage_value: 0,
        total_discount: 0,
        total_expense: 0,
        currency: '',
      }
    );
  }, [summaryData]);

  const formatValue = (value, currency = totals.currency) => {
    return `${currency} ${parseFloat(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const SummaryCard = ({ title, value, subLabel, subValue, icon: Icon, colorClass, isLoading }) => (
    <div className={`${styles.summaryCard} ${colorClass}`}>
      <div className={styles.cardMain}>
        <div className={styles.cardValueSection}>
          <Typography className={styles.cardValue}>
            {isLoading ? (
              <Skeleton
                variant="text"
                width={120}
                height={45}
                animation="wave"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
              />
            ) : (
              formatValue(value)
            )}
          </Typography>
          <Typography className={styles.cardTitle}>{t(title)}</Typography>
        </div>
        <div className={styles.cardIconSection}>
          <Icon className={styles.cardIcon} />
        </div>
      </div>
      <div className={styles.cardFooter}>
        <div className={styles.footerItem}>
          <span className={styles.footerValue}>
            {isLoading ? (
              <Skeleton
                variant="text"
                width={typeof subValue === 'number' ? 40 : 80}
                animation="wave"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
              />
            ) : (
              subValue
            )}
          </span>
          <span className={styles.footerLabel}>{t(subLabel)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.headerSec}>
        <Typography variant="h5" className={styles.pageTitle}>
          All Branches <span className={styles.subTitle}>Dashboard</span>
          {filterDateRange === TIME_RANGE.CUSTOM && customFromDate && customToDate && (
            <span className={styles.customDateSub}>
              {` (${DateTimeHelpers.convertDateToDMY(customFromDate)} - ${DateTimeHelpers.convertDateToDMY(customToDate)})`}
            </span>
          )}
        </Typography>

        <div className={styles.filterSection}>
          {isMobile ? (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FilterAltIcon />}
                onClick={handleFilterClick}
                className={styles.addButtonStyle}
                size="small"
              >
                {t('Filter')}
              </Button>
              <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterClose}>
                {Object.values(TIME_RANGE).map((range) => (
                  <MenuItem key={range} selected={filterDateRange === range} onClick={() => handleFilterSelect(range)}>
                    {t(range.toUpperCase().replace('-', '_'))}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            Object.values(TIME_RANGE).map((range) => (
              <Button
                key={range}
                variant={filterDateRange === range ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => {
                  if (range === TIME_RANGE.CUSTOM) {
                    setCustomDialogOpen(true);
                  } else {
                    setFilterDateRange(range);
                  }
                }}
                className={`${styles.addButtonStyle} ${filterDateRange === range ? styles.activeFilter : ''}`}
                size="small"
              >
                {t(range.toUpperCase().replace('-', '_'))}
              </Button>
            ))
          )}
        </div>
      </div>
      <div className={styles.summaryGrid}>
        <div className={styles.summaryItem}><SummaryCard
            title="Total sales"
            value={totals.total_amount}
            subLabel="Invoices"
            subValue={totals.no_of_invoices}
            icon={ShoppingCartIcon}
            colorClass={styles.blueCard}
            isLoading={isLoading}
          />
        </div>
        <div className={styles.summaryItem}>
          <SummaryCard
            title="Total cost"
            value={totals.total_cost}
            subLabel="Purchase tax"
            subValue={formatValue(totals.total_tax)}
            icon={DescriptionIcon}
            colorClass={styles.orangeCard}
            isLoading={isLoading}
          />
        </div>
        <div className={styles.summaryItem}>
          <SummaryCard
            title="Profit"
            value={totals.profit}
            subLabel="Damage value"
            subValue={formatValue(totals.damage_value)}
            icon={LocalOfferIcon}
            colorClass={styles.tealCard}
            isLoading={isLoading}
          />
        </div>
        <div className={styles.summaryItem}>
          <SummaryCard
            title="Discount"
            value={totals.total_discount}
            subLabel="Expense"
            subValue={formatValue(totals.total_expense)}
            icon={FlashOnIcon}
            colorClass={styles.pinkCard}
            isLoading={isLoading}
          />
        </div>
        <div className={styles.summaryItem}>
          <SummaryCard
            title="Tax"
            value={totals.total_sales_tax}
            subLabel="Additional Tax"
            subValue={formatValue(totals.total_sales_cess)}
            icon={DescriptionIcon}
            colorClass={styles.greenCaerd}
            isLoading={isLoading}
          />
        </div>
      </div>

      <Grid container spacing={1}>
        <Grid item xs={12} md={12}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Shops & Insights
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {isLoading
              ? [...Array(4)].map((_, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                  </Grid>
                ))
              : summaryData.map((shop) => (
                  <Grid item xs={12} md={6} key={shop.shop_id} sx={{ display: 'flex' }}>
                    <Paper className={styles.shopCard}>
                      <div className={styles.shopCardHeader}>
                        <Typography className={styles.shopName}>{shop.shop_name}</Typography>
                        <Typography className={styles.shopTotalSales}>
                          Total sales:{' '}
                          <span className={styles.bold}>{formatValue(shop.total_amount, shop.currency)}</span>
                        </Typography>
                      </div>
                      <div className={styles.shopCardBody}>
                        <div className={styles.shopMetric}>
                          <span className={styles.metricLabel}>Invoices</span>
                          <span className={styles.metricValue}>{shop.no_of_invoices}</span>
                        </div>
                        <div className={styles.metricDivider} />
                        <div className={styles.shopMetric}>
                          <span className={styles.metricLabel}>Cost</span>
                          <span className={styles.metricValue}>{formatValue(shop.total_cost, shop.currency)}</span>
                        </div>
                        <div className={styles.metricDivider} />
                        <div className={styles.shopMetric}>
                          <span className={styles.metricLabel}>Profit</span>
                          <span className={styles.metricValue}>{formatValue(shop.profit, shop.currency)}</span>
                        </div>
                      </div>
                    </Paper>
                  </Grid>
                ))}
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
        <QuickActionCard
          icon={dasboard_item_icon}
          title="Add Product"
          description="Create new products, manage your catalog, and track inventory for all your shops."
          buttonText="Add Product"
          onClick={() => history.push(ROUTES.ITEMS)}
        />
        <QuickActionCard
          icon={dasboard_purchase_icon}
          title="Create Purchase"
          description="Record new purchases from vendors and automatically update your inventory to keep stock levels accurate."
          buttonText="Create Purchase"
          onClick={() => history.push(ROUTES.NEW_PURCHASE)}
        />
        <QuickActionCard
          icon={dasboard_expense_icon}
          title="Add Expense"
          description="Record daily expenses, organize spending, and keep your business finances clear and up to date."
          buttonText="Add Expense"
          onClick={() => history.push(ROUTES.EXPENSES)}
        />
      </Grid>

      <CustomDateFilterDialog
        open={customDialogOpen}
        onClose={() => setCustomDialogOpen(false)}
        onApply={(fromDate, toDate) => {
          setCustomFromDate(fromDate);
          setCustomToDate(toDate);
          setFilterDateRange(TIME_RANGE.CUSTOM);
          setCustomDialogOpen(false);
        }}
        initialFromDate={customFromDate}
        initialToDate={customToDate}
      />
    </div>
  );
}

export default withConsoleBase(MultiShopDashboard);
